import React from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';

/**
 * Extrai todas as URLs (http, https, www) de um texto e remove pontuações finais indesejadas (como ponto final da frase).
 */
export const extractUrlsFromText = (text) => {
  if (!text) return [];
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const matches = text.match(regex) || [];
  const result = [];
  const seen = new Set();

  matches.forEach(rawUrl => {
    let cleanUrl = rawUrl.replace(/[.,;:)]+$/, '');
    let href = cleanUrl;
    if (cleanUrl.toLowerCase().startsWith('www.')) {
      href = `https://${cleanUrl}`;
    }
    if (!seen.has(href)) {
      seen.add(href);
      result.push({ rawUrl, cleanUrl, href });
    }
  });

  return result;
};

/**
 * Componente que renderiza texto com hyperlinks clicáveis e quebras de linha.
 *
 * @param {Object} props
 * @param {string} props.text - O texto a ser formatado.
 * @param {string} [props.className] - Classe CSS adicional.
 */
const LinkifiedText = ({ text, className = '' }) => {
  if (!text) return null;

  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const tokens = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const matchIndex = match.index;
    const matchedText = match[0];

    // Texto normal antes da URL
    if (matchIndex > lastIndex) {
      tokens.push({ type: 'text', value: text.substring(lastIndex, matchIndex) });
    }

    // Limpar pontuação final grudada na URL (ex: ponto final no encerramento da frase)
    let cleanUrl = matchedText.replace(/[.,;:)]+$/, '');
    let trailingPunct = matchedText.substring(cleanUrl.length);
    let href = cleanUrl;
    if (cleanUrl.toLowerCase().startsWith('www.')) {
      href = `https://${cleanUrl}`;
    }

    tokens.push({ type: 'link', url: cleanUrl, href });
    if (trailingPunct) {
      tokens.push({ type: 'text', value: trailingPunct });
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.substring(lastIndex) });
  }

  return (
    <span className={`linkified-text-container ${className}`}>
      {tokens.map((token, idx) => {
        if (token.type === 'link') {
          return (
            <a
              key={idx}
              href={token.href}
              target="_blank"
              rel="noopener noreferrer"
              className="auto-hyperlink"
              onClick={(e) => e.stopPropagation()}
              title={`Abrir link em nova aba: ${token.href}`}
              style={{
                color: '#0284c7',
                fontWeight: '600',
                textDecoration: 'underline',
                wordBreak: 'break-all',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px'
              }}
            >
              {token.url}
              <FaExternalLinkAlt style={{ fontSize: '0.7em', flexShrink: 0 }} />
            </a>
          );
        }

        return token.value.split('\n').map((subPart, subIndex, array) => (
          <React.Fragment key={`${idx}-${subIndex}`}>
            {subPart}
            {subIndex < array.length - 1 && <br />}
          </React.Fragment>
        ));
      })}
    </span>
  );
};

export default LinkifiedText;
